import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "@/utils/translations";
import { localizePath } from "@/utils/languageRoutes";
import { SectionHeading, reveal } from "./demo-shared";

const CEO_IMAGE_URL =
  "https://scmtfyjpaivqbafbtooy.supabase.co/storage/v1/object/public/dcl_website_asset/team/ceo-yongjoo-yoo-duru-compliance.webp";

interface DemoCeoProps {
  language: string;
}

const DemoCeo = ({ language }: DemoCeoProps) => {
  const t = useTranslation(language);
  const isKo = language === "KO";
  const words = t.ceoPreview.message.split(" ");

  return (
    <section className="relative overflow-hidden bg-white py-24 md:py-32">
      <div
        aria-hidden
        className="absolute left-1/2 top-0 h-px w-[min(90%,72rem)] -translate-x-1/2 bg-gradient-to-r from-transparent via-slate-200 to-transparent"
      />
      <div className="container mx-auto max-w-6xl px-4">
        <SectionHeading
          no="01"
          eyebrow={isKo ? "리더십" : "Leadership"}
          title={isKo ? "대표의 한마디" : "A word from our CEO"}
          align="left"
        />

        <div className="mt-14 grid items-center gap-12 lg:grid-cols-[1fr_minmax(260px,0.42fr)] lg:gap-20">
          {/* Progressive word-reveal quote */}
          <figure className="relative">
            <span
              aria-hidden
              className="pointer-events-none absolute -left-4 -top-14 select-none font-serif text-[10rem] leading-none text-primary/10 md:-left-10"
            >
              “
            </span>
            <blockquote className="relative text-xl font-medium leading-relaxed text-slate-800 md:text-[1.7rem] md:leading-[1.6]">
              {words.map((word, index) => (
                <motion.span
                  key={`${word}-${index}`}
                  className="inline-block whitespace-pre"
                  initial={{ opacity: 0.12 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, margin: "-20%" }}
                  transition={{ duration: 0.3, delay: index * 0.014 }}
                >
                  {word}{" "}
                </motion.span>
              ))}
            </blockquote>
            <motion.figcaption {...reveal(0.3)} className="mt-8 flex items-center gap-4">
              <span aria-hidden className="h-px w-10 bg-secondary" />
              <div>
                <p className="font-semibold text-slate-900">Yongjoo Yoo</p>
                <p className="text-sm text-slate-500">{isKo ? "두루컴플라이언스 대표" : "CEO of Duru Compliance"}</p>
              </div>
            </motion.figcaption>
            <motion.div {...reveal(0.4)}>
              <Link
                to={localizePath("/about", language)}
                className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary-dark"
              >
                {t.ceoPreview.cta}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </figure>

          {/* Portrait card */}
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: 3 }}
            whileInView={{ opacity: 1, y: 0, rotate: 2 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ rotate: 0, scale: 1.02 }}
            className="relative mx-auto w-full max-w-xs"
          >
            <div aria-hidden className="absolute -inset-4 -z-10 rounded-[2rem] bg-gradient-to-br from-sky-100 via-transparent to-amber-100" />
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
              <img
                src={CEO_IMAGE_URL}
                alt={t.ceoPreview.imageAlt}
                className="aspect-[2/3] w-full scale-x-[-1] bg-slate-900 object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DemoCeo;
